import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { apiGet, apiPost } from "../../shared/apiClient";
import { normalizeProduct, saveProduct } from "../../shared/productsApi";
import { ImageUploadField } from "../../shared/ImageUploadField";
import { SearchableSelectField } from "../../shared/SearchableSelectField";
import { IconList, IconRefresh, IconSave } from "../../shared/toolbarIcons";

const emptyForm = () => ({
  name: "",
  slug: "",
  brandId: "",
  categoryId: "",
  vendorId: "",
  currencyId: "",
  shortDescription: "",
  longDescription: "",
  basePrice: "",
  salePrice: "",
  barcode: "",
  image: "",
  code: "",
  minQuantity: 1,
  weight: "",
  length: "",
  width: "",
  height: "",
  isActive: true,
  isFeatured: false,
  isDigital: false,
  avgRating: "",
  ratingCount: "",
  metaTitile: "",
  metaDescription: ""
});

const emptyVarientForm = () => ({
  skU: "",
  barcode: "",
  code: "",
  price: 0,
  salePrice: 0,
  balanceQuantity: 0,
  weight: 0,
  length: 0,
  width: 0,
  height: 0,
  isDefault: false,
  isActive: true,
  createdDate: "",
  updatedDate: ""
});

const emptyOptionForm = () => ({
  productVarientId: "",
  attributeId: "",
  attributeValueId: ""
});

const emptyImageForm = () => ({
  image: "",
  isActive: true,
  order: 0
});

function decStr(v) {
  if (v == null || v === "") return "";
  return String(v);
}

function toInputDateTime(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function pickPrimaryVariant(list) {
  if (!list?.length) return null;
  return list.find((v) => v.isDefault) ?? list[0];
}

function pickPrimaryImage(list) {
  if (!list?.length) return null;
  const sorted = [...list].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  return sorted[0];
}

function mapProductToForm(row) {
  const x = normalizeProduct(row) ?? row;
  return {
    name: x.name ?? "",
    slug: x.slug ?? "",
    brandId: x.brandId ?? "",
    categoryId: x.categoryId ?? "",
    vendorId: x.vendorId ?? "",
    currencyId: x.currencyId ?? "",
    shortDescription: x.shortDescription ?? "",
    longDescription: x.longDescription ?? "",
    basePrice: decStr(x.basePrice),
    salePrice: decStr(x.salePrice),
    barcode: x.barcode ?? "",
    image: x.image ?? "",
    code: x.code ?? "",
    minQuantity: x.minQuantity ?? 1,
    weight: decStr(x.weight),
    length: decStr(x.length),
    width: decStr(x.width),
    height: decStr(x.height),
    isActive: Boolean(x.isActive),
    isFeatured: Boolean(x.isFeatured),
    isDigital: Boolean(x.isDigital),
    avgRating: x.avgRating != null ? String(x.avgRating) : "",
    ratingCount: x.ratingCount != null ? String(x.ratingCount) : "",
    metaTitile: x.metaTitile ?? x.metaTitle ?? "",
    metaDescription: x.metaDescription ?? ""
  };
}

function mapVarientToForm(x) {
  return {
    skU: x.skU ?? "",
    barcode: x.barcode ?? "",
    code: x.code ?? "",
    price: x.price ?? 0,
    salePrice: x.salePrice ?? 0,
    balanceQuantity: x.balanceQuantity ?? 0,
    weight: x.weight ?? 0,
    length: x.length ?? 0,
    width: x.width ?? 0,
    height: x.height ?? 0,
    isDefault: Boolean(x.isDefault),
    isActive: Boolean(x.isActive),
    createdDate: toInputDateTime(x.createdDate),
    updatedDate: toInputDateTime(x.updatedDate)
  };
}

function varientFormHasIntent(vf, varientEditingId) {
  if (varientEditingId) return true;
  return Boolean(
    (vf.skU && String(vf.skU).trim()) ||
      (vf.barcode && String(vf.barcode).trim()) ||
      (vf.code && String(vf.code).trim()) ||
      Number(vf.price) !== 0 ||
      Number(vf.salePrice) !== 0 ||
      Number(vf.balanceQuantity) !== 0 ||
      Number(vf.weight) !== 0 ||
      Number(vf.length) !== 0 ||
      Number(vf.width) !== 0 ||
      Number(vf.height) !== 0
  );
}

function optionFormHasIntent(of, optionEditingId) {
  if (optionEditingId) return true;
  return Boolean(of.productVarientId || of.attributeId || of.attributeValueId);
}

function imageFormHasIntent(imf, imageEditingId) {
  if (imageEditingId) return true;
  return Boolean(imf.image?.trim());
}

const emptyTagForm = () => ({
  name: "",
  isActive: true
});

const emptySpecForm = () => ({
  specificationId: "",
  value: "",
  order: 0,
  isActive: true
});

const emptyQuestionForm = () => ({
  customerId: "",
  question: "",
  answer: "",
  isActive: true,
  isRejected: false
});

const emptyReviewForm = () => ({
  orderId: "",
  customerId: "",
  rating: 0,
  review: "",
  createdDate: "",
  updatedDate: ""
});

const emptyReviewImageForm = () => ({
  productReviewId: "",
  image: ""
});

function mapReviewToForm(x) {
  return {
    orderId: x.orderId ?? "",
    customerId: x.customerId ?? "",
    rating: x.rating ?? 0,
    review: x.review ?? "",
    createdDate: toInputDateTime(x.createdDate),
    updatedDate: toInputDateTime(x.updatedDate)
  };
}

function tagFormHasIntent(tf, tagEditingId) {
  if (tagEditingId) return true;
  return Boolean(tf.name?.trim());
}

function specFormHasIntent(sf, specEditingId) {
  if (specEditingId) return true;
  return Boolean(
    (sf.specificationId !== "" && sf.specificationId != null && Number(sf.specificationId) > 0) ||
      sf.value?.trim() ||
      Number(sf.order) !== 0
  );
}

function questionFormHasIntent(qf, questionEditingId) {
  if (questionEditingId) return true;
  return Boolean(qf.customerId || qf.question?.trim() || qf.answer?.trim());
}

function reviewFormHasIntent(rf, reviewEditingId) {
  if (reviewEditingId) return true;
  return Boolean(
    rf.orderId ||
      rf.customerId ||
      Number(rf.rating) > 0 ||
      rf.review?.trim() ||
      (rf.createdDate && String(rf.createdDate).trim()) ||
      (rf.updatedDate && String(rf.updatedDate).trim())
  );
}

function reviewImageFormHasIntent(rif, reviewImageEditingId) {
  if (reviewImageEditingId) return true;
  return Boolean(rif.productReviewId || rif.image?.trim());
}

function pickFirst(list) {
  if (!list?.length) return null;
  return list[0];
}

export function ProductsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const editParam = searchParams.get("edit");
  const varientEditParam = searchParams.get("varientEdit");
  const optionEditParam = searchParams.get("optionEdit");
  const imageEditParam = searchParams.get("imageEdit");
  const tagEditParam = searchParams.get("tagEdit");
  const specEditParam = searchParams.get("specEdit");
  const questionEditParam = searchParams.get("questionEdit");
  const reviewEditParam = searchParams.get("reviewEdit");
  const reviewImageEditParam = searchParams.get("reviewImageEdit");
  const skipVariantHydrateRef = useRef(false);
  const skipImageHydrateRef = useRef(false);
  const skipOptionPrefillRef = useRef(false);
  const skipTagHydrateRef = useRef(false);
  const skipQuestionHydrateRef = useRef(false);
  const skipReviewHydrateRef = useRef(false);
  const skipReviewImageHydrateRef = useRef(false);

  const tabParam = searchParams.get("tab");
  const activeTab =
    tabParam === "variants"
      ? "variants"
      : tabParam === "variant-options"
        ? "variant-options"
        : tabParam === "images"
          ? "images"
          : tabParam === "tags"
            ? "tags"
            : tabParam === "specifications"
              ? "specifications"
              : tabParam === "questions"
                ? "questions"
                : tabParam === "reviews"
                  ? "reviews"
                  : tabParam === "review-images"
                    ? "review-images"
                    : "product";

  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [productVarients, setProductVarients] = useState([]);
  const [productReviews, setProductReviews] = useState([]);
  const [attributes, setAttributes] = useState([]);
  const [attributeValues, setAttributeValues] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [orders, setOrders] = useState([]);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(() => emptyForm());

  const [varientEditingId, setVarientEditingId] = useState(null);
  const [varientForm, setVarientForm] = useState(() => emptyVarientForm());
  const [varientError, setVarientError] = useState(null);

  const [optionEditingId, setOptionEditingId] = useState(null);
  const [optionForm, setOptionForm] = useState(() => emptyOptionForm());
  const [optionError, setOptionError] = useState(null);

  const [imageEditingId, setImageEditingId] = useState(null);
  const [imageForm, setImageForm] = useState(() => emptyImageForm());
  const [imageError, setImageError] = useState(null);

  const [tagEditingId, setTagEditingId] = useState(null);
  const [tagForm, setTagForm] = useState(() => emptyTagForm());
  const [tagError, setTagError] = useState(null);

  const [specEditingId, setSpecEditingId] = useState(null);
  const [specForm, setSpecForm] = useState(() => emptySpecForm());
  const [specError, setSpecError] = useState(null);

  const [questionEditingId, setQuestionEditingId] = useState(null);
  const [questionForm, setQuestionForm] = useState(() => emptyQuestionForm());
  const [questionError, setQuestionError] = useState(null);

  const [reviewEditingId, setReviewEditingId] = useState(null);
  const [reviewForm, setReviewForm] = useState(() => emptyReviewForm());
  const [reviewError, setReviewError] = useState(null);

  const [reviewImageEditingId, setReviewImageEditingId] = useState(null);
  const [reviewImageForm, setReviewImageForm] = useState(() => emptyReviewImageForm());
  const [reviewImageError, setReviewImageError] = useState(null);

  const loadLookups = async () => {
    setLoading(true);
    setError(null);
    const [bRes, cRes, vRes, curRes] = await Promise.all([
      apiGet("/api/Brands"),
      apiGet("/api/Categories"),
      apiGet("/api/Vendors"),
      apiGet("/api/Currencies")
    ]);
    if (bRes.ok) setBrands(bRes.data ?? []);
    if (cRes.ok) setCategories(cRes.data ?? []);
    if (vRes.ok) setVendors(vRes.data ?? []);
    if (curRes.ok) setCurrencies(curRes.data ?? []);
    setLoading(false);
  };

  const loadVariantOptionsLookups = async () => {
    const [aRes, avRes] = await Promise.all([apiGet("/api/Attributes"), apiGet("/api/AttributeValues")]);
    if (aRes.ok) setAttributes(aRes.data ?? []);
    if (avRes.ok) setAttributeValues(avRes.data ?? []);
  };

  const refreshProductVarients = async (productId) => {
    if (!productId) {
      setProductVarients([]);
      return;
    }
    const res = await apiGet(`/api/ProductVarients/by-product/${productId}`);
    if (res.ok) setProductVarients(Array.isArray(res.data) ? res.data : []);
    else setProductVarients([]);
  };

  const refreshProductReviews = async (productId) => {
    if (!productId) {
      setProductReviews([]);
      return;
    }
    const res = await apiGet(`/api/ProductReviews/by-product/${productId}`);
    if (res.ok) setProductReviews(Array.isArray(res.data) ? res.data : []);
    else setProductReviews([]);
  };

  const loadCustomersAndOrders = async () => {
    const [cRes, oRes] = await Promise.all([apiGet("/api/Customers"), apiGet("/api/Orders")]);
    if (cRes.ok) setCustomers(cRes.data ?? []);
    if (oRes.ok) setOrders(oRes.data ?? []);
  };

  useEffect(() => {
    void loadLookups();
  }, []);

  useEffect(() => {
    void loadVariantOptionsLookups();
  }, []);

  useEffect(() => {
    void loadCustomersAndOrders();
  }, []);

  useEffect(() => {
    if (
      !editParam &&
      (varientEditParam ||
        optionEditParam ||
        imageEditParam ||
        tagEditParam ||
        specEditParam ||
        questionEditParam ||
        reviewEditParam ||
        reviewImageEditParam)
    ) {
      const next = new URLSearchParams(searchParams);
      next.delete("varientEdit");
      next.delete("optionEdit");
      next.delete("imageEdit");
      next.delete("tagEdit");
      next.delete("specEdit");
      next.delete("questionEdit");
      next.delete("reviewEdit");
      next.delete("reviewImageEdit");
      setSearchParams(next, { replace: true });
    }
  }, [
    editParam,
    varientEditParam,
    optionEditParam,
    imageEditParam,
    tagEditParam,
    specEditParam,
    questionEditParam,
    reviewEditParam,
    reviewImageEditParam,
    searchParams,
    setSearchParams
  ]);

  useEffect(() => {
    if (!editParam) {
      setEditingId(null);
      setForm(emptyForm());
      setLoading(false);
      setProductVarients([]);
      setProductReviews([]);
      setVarientEditingId(null);
      setVarientForm(emptyVarientForm());
      setOptionEditingId(null);
      setOptionForm(emptyOptionForm());
      setImageEditingId(null);
      setImageForm(emptyImageForm());
      setTagEditingId(null);
      setTagForm(emptyTagForm());
      setSpecEditingId(null);
      setSpecForm(emptySpecForm());
      setQuestionEditingId(null);
      setQuestionForm(emptyQuestionForm());
      setReviewEditingId(null);
      setReviewForm(emptyReviewForm());
      setReviewImageEditingId(null);
      setReviewImageForm(emptyReviewImageForm());
      return;
    }
    const id = Number(editParam);
    if (!Number.isFinite(id)) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      const res = await apiGet(`/api/Products/${id}`);
      if (cancelled) return;
      setLoading(false);
      if (!res.ok) {
        setError(res.error ?? "Failed to load product");
        return;
      }
      const x = res.data;
      setEditingId(x.id);
      setForm(mapProductToForm(x));
      setVarientError(null);
      setOptionError(null);
      setImageError(null);
      setTagError(null);
      setSpecError(null);
      setQuestionError(null);
      setReviewError(null);
      setReviewImageError(null);
      setVarientEditingId(null);
      setVarientForm(emptyVarientForm());
      setOptionEditingId(null);
      setOptionForm(emptyOptionForm());
      setImageEditingId(null);
      setImageForm(emptyImageForm());
      setTagEditingId(null);
      setTagForm(emptyTagForm());
      setSpecEditingId(null);
      setSpecForm(emptySpecForm());
      setQuestionEditingId(null);
      setQuestionForm(emptyQuestionForm());
      setReviewEditingId(null);
      setReviewForm(emptyReviewForm());
      setReviewImageEditingId(null);
      setReviewImageForm(emptyReviewImageForm());
      void refreshProductVarients(x.id);
      void refreshProductReviews(x.id);
    })();
    return () => {
      cancelled = true;
      setLoading(false);
    };
  }, [editParam]);

  useEffect(() => {
    if (!varientEditParam || activeTab !== "variants" || !editingId) return;
    const id = Number(varientEditParam);
    if (!Number.isFinite(id)) return;
    let cancelled = false;
    (async () => {
      const res = await apiGet(`/api/ProductVarients/${id}`);
      if (cancelled) return;
      if (!res.ok) {
        setVarientError(res.error ?? "Failed to load product varient");
        return;
      }
      const x = res.data;
      if (x.productId !== editingId) {
        setVarientError("This varient does not belong to the current product.");
        return;
      }
      setVarientEditingId(x.id);
      setVarientForm(mapVarientToForm(x));
    })();
    return () => {
      cancelled = true;
    };
  }, [varientEditParam, activeTab, editingId]);

  useEffect(() => {
    if (!editingId || activeTab !== "variants" || varientEditParam) return;
    if (skipVariantHydrateRef.current) {
      skipVariantHydrateRef.current = false;
      return;
    }
    let cancelled = false;
    (async () => {
      const res = await apiGet(`/api/ProductVarients/by-product/${editingId}`);
      if (cancelled) return;
      if (!res.ok) {
        setVarientError(res.error ?? "Failed to load varients");
        return;
      }
      const list = Array.isArray(res.data) ? res.data : [];
      const pick = pickPrimaryVariant(list);
      if (!pick) {
        setVarientEditingId(null);
        setVarientForm(emptyVarientForm());
        return;
      }
      setVarientEditingId(pick.id);
      setVarientForm(mapVarientToForm(pick));
    })();
    return () => {
      cancelled = true;
    };
  }, [editingId, activeTab, varientEditParam]);

  useEffect(() => {
    if (!optionEditParam || activeTab !== "variant-options" || !editingId) return;
    const id = Number(optionEditParam);
    if (!Number.isFinite(id)) return;
    let cancelled = false;
    (async () => {
      const res = await apiGet(`/api/ProductVarientOptions/${id}`);
      if (cancelled) return;
      if (!res.ok) {
        setOptionError(res.error ?? "Failed to load varient option");
        return;
      }
      const x = res.data;
      const vRes = await apiGet(`/api/ProductVarients/${x.productVarientId}`);
      if (cancelled) return;
      if (!vRes.ok || vRes.data.productId !== editingId) {
        setOptionError("This option does not belong to the current product.");
        return;
      }
      await refreshProductVarients(editingId);
      setOptionEditingId(x.id);
      setOptionForm({
        productVarientId: x.productVarientId ?? "",
        attributeId: x.attributeId ?? "",
        attributeValueId: x.attributeValueId ?? ""
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [optionEditParam, activeTab, editingId]);

  useEffect(() => {
    if (!editingId || activeTab !== "variant-options" || optionEditParam) return;
    if (skipOptionPrefillRef.current) {
      skipOptionPrefillRef.current = false;
      return;
    }
    const forProduct = productVarients.filter((v) => v.productId === editingId);
    const firstId = forProduct[0]?.id;
    if (!firstId) return;
    setOptionForm((prev) => {
      if (optionEditingId != null) return prev;
      const belongs = forProduct.some((v) => v.id === prev.productVarientId);
      if (prev.productVarientId !== "" && prev.productVarientId != null && belongs) return prev;
      if (prev.productVarientId === firstId) return prev;
      return { ...prev, productVarientId: firstId };
    });
  }, [editingId, activeTab, optionEditParam, productVarients, optionEditingId]);

  useEffect(() => {
    if (!imageEditParam || activeTab !== "images" || !editingId) return;
    const id = Number(imageEditParam);
    if (!Number.isFinite(id)) return;
    let cancelled = false;
    (async () => {
      const res = await apiGet(`/api/ProductImages/${id}`);
      if (cancelled) return;
      if (!res.ok) {
        setImageError(res.error ?? "Failed to load product image");
        return;
      }
      const x = res.data;
      if (x.productId !== editingId) {
        setImageError("This image does not belong to the current product.");
        return;
      }
      setImageEditingId(x.id);
      setImageForm({
        image: x.image ?? "",
        isActive: Boolean(x.isActive),
        order: x.order ?? 0
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [imageEditParam, activeTab, editingId]);

  useEffect(() => {
    if (!editingId || activeTab !== "images" || imageEditParam) return;
    if (skipImageHydrateRef.current) {
      skipImageHydrateRef.current = false;
      return;
    }
    let cancelled = false;
    (async () => {
      const res = await apiGet(`/api/ProductImages/by-product/${editingId}`);
      if (cancelled) return;
      if (!res.ok) {
        setImageError(res.error ?? "Failed to load images");
        return;
      }
      const list = Array.isArray(res.data) ? res.data : [];
      const pick = pickPrimaryImage(list);
      if (!pick) {
        setImageEditingId(null);
        setImageForm(emptyImageForm());
        return;
      }
      setImageEditingId(pick.id);
      setImageForm({
        image: pick.image ?? "",
        isActive: Boolean(pick.isActive),
        order: pick.order ?? 0
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [editingId, activeTab, imageEditParam]);

  useEffect(() => {
    if (!tagEditParam || activeTab !== "tags" || !editingId) return;
    const id = Number(tagEditParam);
    if (!Number.isFinite(id)) return;
    let cancelled = false;
    (async () => {
      const res = await apiGet(`/api/ProductTags/${id}`);
      if (cancelled) return;
      if (!res.ok) {
        setTagError(res.error ?? "Failed to load product tag");
        return;
      }
      const x = res.data;
      if (Number(x.productId) !== Number(editingId)) {
        setTagError("This tag does not belong to the current product.");
        return;
      }
      setTagEditingId(x.id);
      setTagForm({
        name: x.name ?? "",
        isActive: Boolean(x.isActive)
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [tagEditParam, activeTab, editingId]);

  useEffect(() => {
    if (!editingId || activeTab !== "tags" || tagEditParam) return;
    if (skipTagHydrateRef.current) {
      skipTagHydrateRef.current = false;
      return;
    }
    let cancelled = false;
    (async () => {
      const res = await apiGet(`/api/ProductTags/by-product/${editingId}`);
      if (cancelled) return;
      if (!res.ok) {
        setTagError(res.error ?? "Failed to load tags");
        return;
      }
      const list = Array.isArray(res.data) ? res.data : [];
      const pick = pickFirst(list);
      if (!pick) {
        setTagEditingId(null);
        setTagForm(emptyTagForm());
        return;
      }
      setTagEditingId(pick.id);
      setTagForm({
        name: pick.name ?? "",
        isActive: Boolean(pick.isActive)
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [editingId, activeTab, tagEditParam]);

  useEffect(() => {
    if (!specEditParam || activeTab !== "specifications") return;
    const id = Number(specEditParam);
    if (!Number.isFinite(id)) return;
    let cancelled = false;
    (async () => {
      const res = await apiGet(`/api/ProductSpecifications/${id}`);
      if (cancelled) return;
      if (!res.ok) {
        setSpecError(res.error ?? "Failed to load specification");
        return;
      }
      const x = res.data;
      setSpecEditingId(x.id);
      setSpecForm({
        specificationId: x.specificationId ?? "",
        value: x.value ?? "",
        order: x.order ?? 0,
        isActive: Boolean(x.isActive)
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [specEditParam, activeTab]);

  useEffect(() => {
    if (!questionEditParam || activeTab !== "questions" || !editingId) return;
    const id = Number(questionEditParam);
    if (!Number.isFinite(id)) return;
    let cancelled = false;
    (async () => {
      const res = await apiGet(`/api/ProductQuestions/${id}`);
      if (cancelled) return;
      if (!res.ok) {
        setQuestionError(res.error ?? "Failed to load question");
        return;
      }
      const x = res.data;
      if (Number(x.productId) !== Number(editingId)) {
        setQuestionError("This question does not belong to the current product.");
        return;
      }
      setQuestionEditingId(x.id);
      setQuestionForm({
        customerId: x.customerId ?? "",
        question: x.question ?? "",
        answer: x.answer ?? "",
        isActive: Boolean(x.isActive),
        isRejected: Boolean(x.isRejected)
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [questionEditParam, activeTab, editingId]);

  useEffect(() => {
    if (!editingId || activeTab !== "questions" || questionEditParam) return;
    if (skipQuestionHydrateRef.current) {
      skipQuestionHydrateRef.current = false;
      return;
    }
    let cancelled = false;
    (async () => {
      const res = await apiGet(`/api/ProductQuestions/by-product/${editingId}`);
      if (cancelled) return;
      if (!res.ok) {
        setQuestionError(res.error ?? "Failed to load questions");
        return;
      }
      const list = Array.isArray(res.data) ? res.data : [];
      const pick = pickFirst(list);
      if (!pick) {
        setQuestionEditingId(null);
        setQuestionForm(emptyQuestionForm());
        return;
      }
      setQuestionEditingId(pick.id);
      setQuestionForm({
        customerId: pick.customerId ?? "",
        question: pick.question ?? "",
        answer: pick.answer ?? "",
        isActive: Boolean(pick.isActive),
        isRejected: Boolean(pick.isRejected)
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [editingId, activeTab, questionEditParam]);

  useEffect(() => {
    if (!reviewEditParam || activeTab !== "reviews" || !editingId) return;
    const id = Number(reviewEditParam);
    if (!Number.isFinite(id)) return;
    let cancelled = false;
    (async () => {
      const res = await apiGet(`/api/ProductReviews/${id}`);
      if (cancelled) return;
      if (!res.ok) {
        setReviewError(res.error ?? "Failed to load review");
        return;
      }
      const x = res.data;
      if (Number(x.productId) !== Number(editingId)) {
        setReviewError("This review does not belong to the current product.");
        return;
      }
      setReviewEditingId(x.id);
      setReviewForm(mapReviewToForm(x));
    })();
    return () => {
      cancelled = true;
    };
  }, [reviewEditParam, activeTab, editingId]);

  useEffect(() => {
    if (!editingId || activeTab !== "reviews" || reviewEditParam) return;
    if (skipReviewHydrateRef.current) {
      skipReviewHydrateRef.current = false;
      return;
    }
    let cancelled = false;
    (async () => {
      const res = await apiGet(`/api/ProductReviews/by-product/${editingId}`);
      if (cancelled) return;
      if (!res.ok) {
        setReviewError(res.error ?? "Failed to load reviews");
        return;
      }
      const list = Array.isArray(res.data) ? res.data : [];
      const pick = pickFirst(list);
      if (!pick) {
        setReviewEditingId(null);
        setReviewForm(emptyReviewForm());
        return;
      }
      setReviewEditingId(pick.id);
      setReviewForm(mapReviewToForm(pick));
    })();
    return () => {
      cancelled = true;
    };
  }, [editingId, activeTab, reviewEditParam]);

  useEffect(() => {
    if (!reviewImageEditParam || activeTab !== "review-images" || !editingId) return;
    const id = Number(reviewImageEditParam);
    if (!Number.isFinite(id)) return;
    let cancelled = false;
    (async () => {
      const res = await apiGet(`/api/ProductReviewImages/${id}`);
      if (cancelled) return;
      if (!res.ok) {
        setReviewImageError(res.error ?? "Failed to load review image");
        return;
      }
      const x = res.data;
      const rv = await apiGet(`/api/ProductReviews/${x.productReviewId}`);
      if (cancelled) return;
      if (!rv.ok || Number(rv.data.productId) !== Number(editingId)) {
        setReviewImageError("This image does not belong to a review for the current product.");
        return;
      }
      setReviewImageEditingId(x.id);
      setReviewImageForm({
        productReviewId: x.productReviewId ?? "",
        image: x.image ?? ""
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [reviewImageEditParam, activeTab, editingId]);

  useEffect(() => {
    if (!editingId || activeTab !== "review-images" || reviewImageEditParam) return;
    if (skipReviewImageHydrateRef.current) {
      skipReviewImageHydrateRef.current = false;
      return;
    }
    let cancelled = false;
    (async () => {
      const rRes = await apiGet(`/api/ProductReviews/by-product/${editingId}`);
      if (cancelled) return;
      if (!rRes.ok) {
        setReviewImageError(rRes.error ?? "Failed to load reviews");
        return;
      }
      const reviews = Array.isArray(rRes.data) ? rRes.data : [];
      const pickR = pickFirst(reviews);
      if (!pickR) {
        setReviewImageEditingId(null);
        setReviewImageForm(emptyReviewImageForm());
        return;
      }
      const imgRes = await apiGet(`/api/ProductReviewImages/by-review/${pickR.id}`);
      if (cancelled) return;
      if (!imgRes.ok) {
        setReviewImageError(imgRes.error ?? "Failed to load review images");
        return;
      }
      const imgs = Array.isArray(imgRes.data) ? imgRes.data : [];
      const pickI = pickFirst(imgs);
      if (!pickI) {
        setReviewImageEditingId(null);
        setReviewImageForm({ productReviewId: pickR.id, image: "" });
        return;
      }
      setReviewImageEditingId(pickI.id);
      setReviewImageForm({
        productReviewId: pickI.productReviewId ?? pickR.id,
        image: pickI.image ?? ""
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [editingId, activeTab, reviewImageEditParam]);

  const selectTab = (tab) => {
    const next = new URLSearchParams(searchParams);
    if (tab === "product") {
      next.delete("tab");
    } else {
      next.set("tab", tab);
    }
    next.delete("varientEdit");
    next.delete("optionEdit");
    next.delete("imageEdit");
    next.delete("tagEdit");
    next.delete("specEdit");
    next.delete("questionEdit");
    next.delete("reviewEdit");
    next.delete("reviewImageEdit");
    setSearchParams(next, { replace: true });
    setVarientError(null);
    setOptionError(null);
    setImageError(null);
    setTagError(null);
    setSpecError(null);
    setQuestionError(null);
    setReviewError(null);
    setReviewImageError(null);
  };

  const saveAll = async () => {
    if (!form.brandId || !form.categoryId || !form.currencyId || !form.vendorId || !form.name.trim()) {
      setError("Name, brand, category, currency, and vendor are required.");
      return;
    }

    const variantIntent = varientFormHasIntent(varientForm, varientEditingId);
    const optionIntent = optionFormHasIntent(optionForm, optionEditingId);
    const galleryIntent = imageFormHasIntent(imageForm, imageEditingId);
    const tagIntent = tagFormHasIntent(tagForm, tagEditingId);
    const specIntent = specFormHasIntent(specForm, specEditingId);
    const questionIntent = questionFormHasIntent(questionForm, questionEditingId);
    const reviewIntent = reviewFormHasIntent(reviewForm, reviewEditingId);
    const reviewImageIntent = reviewImageFormHasIntent(reviewImageForm, reviewImageEditingId);

    if (optionIntent) {
      if (!optionForm.attributeId || !optionForm.attributeValueId) {
        setError(null);
        setOptionError("Attribute and attribute value are required—or clear the varient option fields.");
        return;
      }
      const hasPv = optionForm.productVarientId !== "" && optionForm.productVarientId != null && Number(optionForm.productVarientId) > 0;
      if (!hasPv && !variantIntent) {
        setError(null);
        setOptionError("Select a product varient, or add one on the Varients tab in the same save.");
        return;
      }
    }

    if (galleryIntent && !imageForm.image?.trim()) {
      setError(null);
      setImageError("Gallery image URL is required—or clear the product image fields.");
      return;
    }

    if (tagIntent && !tagForm.name?.trim()) {
      setError(null);
      setTagError("Tag name is required—or clear the tag fields.");
      return;
    }

    if (specIntent && (!specForm.specificationId || Number(specForm.specificationId) <= 0)) {
      setError(null);
      setSpecError("Specification ID is required—or clear the specification fields.");
      return;
    }

    if (questionIntent && !questionForm.customerId) {
      setError(null);
      setQuestionError("Customer is required—or clear the question fields.");
      return;
    }

    if (reviewIntent && (!reviewForm.orderId || !reviewForm.customerId)) {
      setError(null);
      setReviewError("Order and customer are required—or clear the review fields.");
      return;
    }

    if (reviewImageIntent && !reviewImageForm.image?.trim()) {
      setError(null);
      setReviewImageError("Review image URL is required—or clear the review image fields.");
      return;
    }

    if (reviewImageIntent && !reviewIntent) {
      const hasPr =
        reviewImageForm.productReviewId !== "" &&
        reviewImageForm.productReviewId != null &&
        Number(reviewImageForm.productReviewId) > 0;
      if (!hasPr) {
        setError(null);
        setReviewImageError("Select a product review, or add a review on the Reviews tab in the same save.");
        return;
      }
    }

    setSaving(true);
    setError(null);
    setVarientError(null);
    setOptionError(null);
    setImageError(null);
    setTagError(null);
    setSpecError(null);
    setQuestionError(null);
    setReviewError(null);
    setReviewImageError(null);

    const priorProductId = editingId;

    const productRes = await saveProduct(form, priorProductId);

    if (!productRes.ok) {
      setError(productRes.error ?? "Failed to save product");
      setSaving(false);
      return;
    }

    const productId = priorProductId ?? productRes.data?.id ?? productRes.data?.Id;
    if (!productId) {
      setError("Product was saved but no id was returned.");
      setSaving(false);
      return;
    }

    const productIdNum = Number(productId);
    let resolvedVariantId =
      varientEditingId != null && Number(varientEditingId) > 0 ? Number(varientEditingId) : null;

    if (variantIntent) {
      const created =
        varientForm.createdDate && !Number.isNaN(new Date(varientForm.createdDate).getTime())
          ? new Date(varientForm.createdDate).toISOString()
          : new Date().toISOString();
      const updated =
        varientForm.updatedDate && !Number.isNaN(new Date(varientForm.updatedDate).getTime())
          ? new Date(varientForm.updatedDate).toISOString()
          : null;
      const vRes = await apiPost("/api/ProductVarients", {
        id: varientEditingId ?? 0,
        productId: productIdNum,
        skU: varientForm.skU || null,
        barcode: varientForm.barcode || null,
        code: varientForm.code || null,
        price: Number(varientForm.price || 0),
        salePrice: Number(varientForm.salePrice || 0),
        balanceQuantity: Number(varientForm.balanceQuantity || 0),
        weight: Number(varientForm.weight || 0),
        length: Number(varientForm.length || 0),
        width: Number(varientForm.width || 0),
        height: Number(varientForm.height || 0),
        isDefault: Boolean(varientForm.isDefault),
        isActive: Boolean(varientForm.isActive),
        createdDate: created,
        updatedDate: updated
      });
      if (!vRes.ok) {
        setVarientError(vRes.error ?? "Failed to save product varient");
        setSaving(false);
        return;
      }
      const vid = vRes.data?.id ?? vRes.data?.Id;
      if (vid) resolvedVariantId = Number(vid);
    }

    if (optionIntent) {
      let pvId = Number(optionForm.productVarientId);
      if (!Number.isFinite(pvId) || pvId <= 0) {
        if (resolvedVariantId) {
          pvId = resolvedVariantId;
        } else {
          setOptionError("Select a product varient for this option.");
          setSaving(false);
          return;
        }
      } else {
        const vCheck = await apiGet(`/api/ProductVarients/${pvId}`);
        if (!vCheck.ok || Number(vCheck.data.productId) !== productIdNum) {
          setOptionError("Selected varient does not belong to this product.");
          setSaving(false);
          return;
        }
      }
      const oRes = await apiPost("/api/ProductVarientOptions", {
        id: optionEditingId ?? 0,
        productVarientId: pvId,
        attributeId: Number(optionForm.attributeId),
        attributeValueId: Number(optionForm.attributeValueId)
      });
      if (!oRes.ok) {
        setOptionError(oRes.error ?? "Failed to save varient option");
        setSaving(false);
        return;
      }
    }

    if (galleryIntent) {
      const iRes = await apiPost("/api/ProductImages", {
        id: imageEditingId ?? 0,
        productId: productIdNum,
        image: imageForm.image || null,
        isActive: imageForm.isActive,
        order: Number(imageForm.order || 0)
      });
      if (!iRes.ok) {
        setImageError(iRes.error ?? "Failed to save product image");
        setSaving(false);
        return;
      }
    }

    let resolvedReviewId = reviewEditingId != null && Number(reviewEditingId) > 0 ? Number(reviewEditingId) : null;

    if (tagIntent) {
      const tRes = await apiPost("/api/ProductTags", {
        id: tagEditingId ?? 0,
        name: tagForm.name || null,
        productId: productIdNum,
        isActive: Boolean(tagForm.isActive)
      });
      if (!tRes.ok) {
        setTagError(tRes.error ?? "Failed to save product tag");
        setSaving(false);
        return;
      }
    }

    if (specIntent) {
      const sRes = await apiPost("/api/ProductSpecifications", {
        id: specEditingId ?? 0,
        specificationId: Number(specForm.specificationId),
        value: specForm.value || null,
        order: Number(specForm.order || 0),
        isActive: Boolean(specForm.isActive)
      });
      if (!sRes.ok) {
        setSpecError(sRes.error ?? "Failed to save product specification");
        setSaving(false);
        return;
      }
    }

    if (questionIntent) {
      const qRes = await apiPost("/api/ProductQuestions", {
        id: questionEditingId ?? 0,
        customerId: Number(questionForm.customerId),
        productId: productIdNum,
        question: questionForm.question || null,
        answer: questionForm.answer || null,
        isActive: Boolean(questionForm.isActive),
        isRejected: Boolean(questionForm.isRejected)
      });
      if (!qRes.ok) {
        setQuestionError(qRes.error ?? "Failed to save product question");
        setSaving(false);
        return;
      }
    }

    if (reviewIntent) {
      const created =
        reviewForm.createdDate && !Number.isNaN(new Date(reviewForm.createdDate).getTime())
          ? new Date(reviewForm.createdDate).toISOString()
          : new Date().toISOString();
      const updated =
        reviewForm.updatedDate && !Number.isNaN(new Date(reviewForm.updatedDate).getTime())
          ? new Date(reviewForm.updatedDate).toISOString()
          : null;
      const rRes = await apiPost("/api/ProductReviews", {
        id: reviewEditingId ?? 0,
        orderId: Number(reviewForm.orderId),
        productId: productIdNum,
        rating: Number(reviewForm.rating || 0),
        review: reviewForm.review || null,
        customerId: Number(reviewForm.customerId),
        createdDate: created,
        updatedDate: updated
      });
      if (!rRes.ok) {
        setReviewError(rRes.error ?? "Failed to save product review");
        setSaving(false);
        return;
      }
      const rid = rRes.data?.id ?? rRes.data?.Id;
      if (rid) resolvedReviewId = Number(rid);
    }

    if (reviewImageIntent) {
      let prId = Number(reviewImageForm.productReviewId);
      if (!Number.isFinite(prId) || prId <= 0) {
        if (resolvedReviewId) {
          prId = resolvedReviewId;
        } else {
          setReviewImageError("Select a product review for this image.");
          setSaving(false);
          return;
        }
      }
      const rvCheck = await apiGet(`/api/ProductReviews/${prId}`);
      if (!rvCheck.ok || Number(rvCheck.data.productId) !== productIdNum) {
        setReviewImageError("Selected review does not belong to this product.");
        setSaving(false);
        return;
      }
      const riRes = await apiPost("/api/ProductReviewImages", {
        id: reviewImageEditingId ?? 0,
        productReviewId: prId,
        image: reviewImageForm.image || null
      });
      if (!riRes.ok) {
        setReviewImageError(riRes.error ?? "Failed to save review image");
        setSaving(false);
        return;
      }
    }

    setSaving(false);
    navigate("/products-list");
  };

  const onRefreshProduct = async () => {
    setError(null);
    if (editParam) {
      const id = Number(editParam);
      if (!Number.isFinite(id)) return;
      setLoading(true);
      const res = await apiGet(`/api/Products/${id}`);
      setLoading(false);
      if (!res.ok) {
        setError(res.error ?? "Failed to load product");
        return;
      }
      setEditingId(res.data.id);
      setForm(mapProductToForm(res.data));
      void refreshProductVarients(res.data.id);
      void refreshProductReviews(res.data.id);
    } else {
      setEditingId(null);
      setForm(emptyForm());
      setProductVarients([]);
      setProductReviews([]);
      setVarientEditingId(null);
      setVarientForm(emptyVarientForm());
      setOptionEditingId(null);
      setOptionForm(emptyOptionForm());
      setImageEditingId(null);
      setImageForm(emptyImageForm());
      setTagEditingId(null);
      setTagForm(emptyTagForm());
      setSpecEditingId(null);
      setSpecForm(emptySpecForm());
      setQuestionEditingId(null);
      setQuestionForm(emptyQuestionForm());
      setReviewEditingId(null);
      setReviewForm(emptyReviewForm());
      setReviewImageEditingId(null);
      setReviewImageForm(emptyReviewImageForm());
    }
  };

  const onToolbarRefresh = async () => {
    if (activeTab === "product") {
      await onRefreshProduct();
      return;
    }
    if (!editingId) return;
    if (activeTab === "variants") {
      setVarientError(null);
      const aid = searchParams.get("varientEdit");
      if (aid) {
        const res = await apiGet(`/api/ProductVarients/${Number(aid)}`);
        if (res.ok && res.data.productId === editingId) {
          setVarientEditingId(res.data.id);
          setVarientForm(mapVarientToForm(res.data));
        }
      } else {
        const res = await apiGet(`/api/ProductVarients/by-product/${editingId}`);
        if (!res.ok) {
          setVarientError(res.error ?? "Failed to load varients");
          return;
        }
        const list = Array.isArray(res.data) ? res.data : [];
        const pick = pickPrimaryVariant(list);
        if (!pick) {
          setVarientEditingId(null);
          setVarientForm(emptyVarientForm());
        } else {
          setVarientEditingId(pick.id);
          setVarientForm(mapVarientToForm(pick));
        }
      }
      return;
    }
    if (activeTab === "variant-options") {
      setOptionError(null);
      await loadVariantOptionsLookups();
      const vListRes = await apiGet(`/api/ProductVarients/by-product/${editingId}`);
      const vList = vListRes.ok && Array.isArray(vListRes.data) ? vListRes.data : [];
      setProductVarients(vList);
      const oid = searchParams.get("optionEdit");
      if (oid) {
        const res = await apiGet(`/api/ProductVarientOptions/${Number(oid)}`);
        if (res.ok) {
          const x = res.data;
          const vRes = await apiGet(`/api/ProductVarients/${x.productVarientId}`);
          if (vRes.ok && vRes.data.productId === editingId) {
            setOptionEditingId(x.id);
            setOptionForm({
              productVarientId: x.productVarientId ?? "",
              attributeId: x.attributeId ?? "",
              attributeValueId: x.attributeValueId ?? ""
            });
          }
        }
      }
      return;
    }
    if (activeTab === "images") {
      setImageError(null);
      const iid = searchParams.get("imageEdit");
      if (iid) {
        const res = await apiGet(`/api/ProductImages/${Number(iid)}`);
        if (res.ok && res.data.productId === editingId) {
          setImageEditingId(res.data.id);
          setImageForm({
            image: res.data.image ?? "",
            isActive: Boolean(res.data.isActive),
            order: res.data.order ?? 0
          });
        }
      } else {
        const res = await apiGet(`/api/ProductImages/by-product/${editingId}`);
        if (!res.ok) {
          setImageError(res.error ?? "Failed to load images");
          return;
        }
        const list = Array.isArray(res.data) ? res.data : [];
        const pick = pickPrimaryImage(list);
        if (!pick) {
          setImageEditingId(null);
          setImageForm(emptyImageForm());
        } else {
          setImageEditingId(pick.id);
          setImageForm({
            image: pick.image ?? "",
            isActive: Boolean(pick.isActive),
            order: pick.order ?? 0
          });
        }
      }
      return;
    }
    if (activeTab === "tags") {
      setTagError(null);
      const tid = searchParams.get("tagEdit");
      if (tid) {
        const res = await apiGet(`/api/ProductTags/${Number(tid)}`);
        if (res.ok && Number(res.data.productId) === Number(editingId)) {
          setTagEditingId(res.data.id);
          setTagForm({ name: res.data.name ?? "", isActive: Boolean(res.data.isActive) });
        }
      } else {
        const res = await apiGet(`/api/ProductTags/by-product/${editingId}`);
        if (!res.ok) {
          setTagError(res.error ?? "Failed to load tags");
          return;
        }
        const list = Array.isArray(res.data) ? res.data : [];
        const pick = pickFirst(list);
        if (!pick) {
          setTagEditingId(null);
          setTagForm(emptyTagForm());
        } else {
          setTagEditingId(pick.id);
          setTagForm({ name: pick.name ?? "", isActive: Boolean(pick.isActive) });
        }
      }
      return;
    }
    if (activeTab === "specifications") {
      setSpecError(null);
      const sid = searchParams.get("specEdit");
      if (sid) {
        const res = await apiGet(`/api/ProductSpecifications/${Number(sid)}`);
        if (res.ok) {
          setSpecEditingId(res.data.id);
          setSpecForm({
            specificationId: res.data.specificationId ?? "",
            value: res.data.value ?? "",
            order: res.data.order ?? 0,
            isActive: Boolean(res.data.isActive)
          });
        }
      } else {
        setSpecEditingId(null);
        setSpecForm(emptySpecForm());
      }
      return;
    }
    if (activeTab === "questions") {
      setQuestionError(null);
      await loadCustomersAndOrders();
      const qid = searchParams.get("questionEdit");
      if (qid) {
        const res = await apiGet(`/api/ProductQuestions/${Number(qid)}`);
        if (res.ok && Number(res.data.productId) === Number(editingId)) {
          const x = res.data;
          setQuestionEditingId(x.id);
          setQuestionForm({
            customerId: x.customerId ?? "",
            question: x.question ?? "",
            answer: x.answer ?? "",
            isActive: Boolean(x.isActive),
            isRejected: Boolean(x.isRejected)
          });
        }
      } else {
        const res = await apiGet(`/api/ProductQuestions/by-product/${editingId}`);
        if (!res.ok) {
          setQuestionError(res.error ?? "Failed to load questions");
          return;
        }
        const list = Array.isArray(res.data) ? res.data : [];
        const pick = pickFirst(list);
        if (!pick) {
          setQuestionEditingId(null);
          setQuestionForm(emptyQuestionForm());
        } else {
          setQuestionEditingId(pick.id);
          setQuestionForm({
            customerId: pick.customerId ?? "",
            question: pick.question ?? "",
            answer: pick.answer ?? "",
            isActive: Boolean(pick.isActive),
            isRejected: Boolean(pick.isRejected)
          });
        }
      }
      return;
    }
    if (activeTab === "reviews") {
      setReviewError(null);
      await loadCustomersAndOrders();
      const rid = searchParams.get("reviewEdit");
      if (rid) {
        const res = await apiGet(`/api/ProductReviews/${Number(rid)}`);
        if (res.ok && Number(res.data.productId) === Number(editingId)) {
          setReviewEditingId(res.data.id);
          setReviewForm(mapReviewToForm(res.data));
        }
      } else {
        const res = await apiGet(`/api/ProductReviews/by-product/${editingId}`);
        if (!res.ok) {
          setReviewError(res.error ?? "Failed to load reviews");
          return;
        }
        const list = Array.isArray(res.data) ? res.data : [];
        const pick = pickFirst(list);
        if (!pick) {
          setReviewEditingId(null);
          setReviewForm(emptyReviewForm());
        } else {
          setReviewEditingId(pick.id);
          setReviewForm(mapReviewToForm(pick));
        }
      }
      void refreshProductReviews(editingId);
      return;
    }
    if (activeTab === "review-images") {
      setReviewImageError(null);
      void refreshProductReviews(editingId);
      const riid = searchParams.get("reviewImageEdit");
      if (riid) {
        const res = await apiGet(`/api/ProductReviewImages/${Number(riid)}`);
        if (res.ok) {
          const rv = await apiGet(`/api/ProductReviews/${res.data.productReviewId}`);
          if (rv.ok && Number(rv.data.productId) === Number(editingId)) {
            setReviewImageEditingId(res.data.id);
            setReviewImageForm({
              productReviewId: res.data.productReviewId ?? "",
              image: res.data.image ?? ""
            });
          }
        }
      } else {
        const rRes = await apiGet(`/api/ProductReviews/by-product/${editingId}`);
        const reviews = rRes.ok && Array.isArray(rRes.data) ? rRes.data : [];
        const pickR = pickFirst(reviews);
        if (!pickR) {
          setReviewImageEditingId(null);
          setReviewImageForm(emptyReviewImageForm());
          return;
        }
        const imgRes = await apiGet(`/api/ProductReviewImages/by-review/${pickR.id}`);
        const imgs = imgRes.ok && Array.isArray(imgRes.data) ? imgRes.data : [];
        const pickI = pickFirst(imgs);
        if (!pickI) {
          setReviewImageEditingId(null);
          setReviewImageForm({ productReviewId: pickR.id, image: "" });
        } else {
          setReviewImageEditingId(pickI.id);
          setReviewImageForm({
            productReviewId: pickI.productReviewId ?? pickR.id,
            image: pickI.image ?? ""
          });
        }
      }
    }
  };

  const varientsForProduct = productVarients.filter((v) => Number(v.productId) === Number(editingId));

  return (
    <div className="card">
      <div className="card-header card-header--row">
        <div>
          <h2>{editingId ? "Edit Product" : "Add Product"}</h2>
        </div>
        <div className="card-header-actions">
          <button
            className="icon-button icon-button--toolbar"
            type="button"
            onClick={() => void saveAll()}
            disabled={saving || loading}
            title="Save product and all related tab data"
            aria-label="Save"
          >
            <IconSave />
          </button>
          <button
            className="icon-button icon-button--toolbar"
            type="button"
            onClick={() => void onToolbarRefresh()}
            disabled={saving || loading}
            title="Refresh"
            aria-label="Refresh"
          >
            <IconRefresh />
          </button>
          <Link to="/products-list" className="icon-button icon-button--toolbar" title="Product list" aria-label="Open product list">
            <IconList />
          </Link>
        </div>
      </div>

      <div className="card-body">
        <div className="form-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            className={`form-tab ${activeTab === "product" ? "form-tab--active" : ""}`}
            aria-selected={activeTab === "product"}
            onClick={() => selectTab("product")}
          >
            Product
          </button>
          <button
            type="button"
            role="tab"
            className={`form-tab ${activeTab === "variants" ? "form-tab--active" : ""}`}
            aria-selected={activeTab === "variants"}
            onClick={() => selectTab("variants")}
          >
            Product varients
          </button>
          <button
            type="button"
            role="tab"
            className={`form-tab ${activeTab === "variant-options" ? "form-tab--active" : ""}`}
            aria-selected={activeTab === "variant-options"}
            onClick={() => selectTab("variant-options")}
          >
            Varient options
          </button>
          <button
            type="button"
            role="tab"
            className={`form-tab ${activeTab === "images" ? "form-tab--active" : ""}`}
            aria-selected={activeTab === "images"}
            onClick={() => selectTab("images")}
          >
            Product images
          </button>
          <button
            type="button"
            role="tab"
            className={`form-tab ${activeTab === "tags" ? "form-tab--active" : ""}`}
            aria-selected={activeTab === "tags"}
            onClick={() => selectTab("tags")}
          >
            Product tags
          </button>
          <button
            type="button"
            role="tab"
            className={`form-tab ${activeTab === "specifications" ? "form-tab--active" : ""}`}
            aria-selected={activeTab === "specifications"}
            onClick={() => selectTab("specifications")}
          >
            Specifications
          </button>
          <button
            type="button"
            role="tab"
            className={`form-tab ${activeTab === "questions" ? "form-tab--active" : ""}`}
            aria-selected={activeTab === "questions"}
            onClick={() => selectTab("questions")}
          >
            Questions
          </button>
          <button
            type="button"
            role="tab"
            className={`form-tab ${activeTab === "reviews" ? "form-tab--active" : ""}`}
            aria-selected={activeTab === "reviews"}
            onClick={() => selectTab("reviews")}
          >
            Reviews
          </button>
          <button
            type="button"
            role="tab"
            className={`form-tab ${activeTab === "review-images" ? "form-tab--active" : ""}`}
            aria-selected={activeTab === "review-images"}
            onClick={() => selectTab("review-images")}
          >
            Review images
          </button>
        </div>

        {activeTab === "product" && (
          <form
            id="products-form"
            className="form form-fields-bottomline"
            onSubmit={(e) => {
              e.preventDefault();
              void saveAll();
            }}
          >
            <div className="form-row form-row--4">
              <label className="field">
                <span>Name</span>
                <input type="text" value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} />
              </label>
              <label className="field">
                <span>Slug</span>
                <input type="text" value={form.slug} onChange={(e) => setForm((p) => ({ ...p, slug: e.target.value }))} />
              </label>
              <label className="field">
                <span>Code</span>
                <input type="text" value={form.code} onChange={(e) => setForm((p) => ({ ...p, code: e.target.value }))} />
              </label>
              <label className="field">
                <span>Barcode</span>
                <input type="text" value={form.barcode} onChange={(e) => setForm((p) => ({ ...p, barcode: e.target.value }))} />
              </label>
            </div>
            <div className="form-row form-row--2">
              <label className="field">
                <span>Short description</span>
                <input type="text" value={form.shortDescription} onChange={(e) => setForm((p) => ({ ...p, shortDescription: e.target.value }))} />
              </label>
              <label className="field">
                <span>Long description</span>
                <textarea rows={1} value={form.longDescription} onChange={(e) => setForm((p) => ({ ...p, longDescription: e.target.value }))} />
              </label>
            </div>
            <div className="form-row form-row--4">
              <SearchableSelectField
                label="Brand"
                options={brands}
                value={form.brandId}
                onChange={(nextValue) => setForm((p) => ({ ...p, brandId: nextValue === "" ? "" : Number(nextValue) }))}
                getOptionLabel={(option) => option.name || `Brand #${option.id}`}
                getSearchText={(option) => `${option.name ?? ""} ${option.id ?? ""}`}
                placeholder="Search brand"
              />
              <SearchableSelectField
                label="Category"
                options={categories}
                value={form.categoryId}
                onChange={(nextValue) => setForm((p) => ({ ...p, categoryId: nextValue === "" ? "" : Number(nextValue) }))}
                getOptionLabel={(option) => option.name || `Category #${option.id}`}
                getSearchText={(option) => `${option.name ?? ""} ${option.id ?? ""}`}
                placeholder="Search category"
              />
              <SearchableSelectField
                label="Vendor"
                options={vendors}
                value={form.vendorId}
                onChange={(nextValue) => setForm((p) => ({ ...p, vendorId: nextValue === "" ? "" : Number(nextValue) }))}
                getOptionLabel={(option) => option.name || `Vendor #${option.id}`}
                getSearchText={(option) => `${option.name ?? ""} ${option.code ?? ""} ${option.id ?? ""}`}
                placeholder="Search vendor"
              />
              <SearchableSelectField
                label="Currency"
                options={currencies}
                value={form.currencyId}
                onChange={(nextValue) => setForm((p) => ({ ...p, currencyId: nextValue === "" ? "" : Number(nextValue) }))}
                getOptionLabel={(option) => option.code || option.name || `Currency #${option.id}`}
                getSearchText={(option) => `${option.code ?? ""} ${option.name ?? ""} ${option.symbol ?? ""} ${option.id ?? ""}`}
                placeholder="Search currency"
              />
            </div>
            <div className="form-row form-row--4">
              <label className="field">
                <span>Base Price</span>
                <input type="number" value={form.basePrice} onChange={(e) => setForm((p) => ({ ...p, basePrice: e.target.value }))} />
              </label>
              <label className="field">
                <span>Sale Price</span>
                <input type="number" value={form.salePrice} onChange={(e) => setForm((p) => ({ ...p, salePrice: e.target.value }))} />
              </label>
              <label className="field">
                <span>Min Quantity</span>
                <input type="number" value={form.minQuantity} onChange={(e) => setForm((p) => ({ ...p, minQuantity: e.target.value }))} />
              </label>
              <label className="field">
                <span>Weight</span>
                <input type="number" value={form.weight} onChange={(e) => setForm((p) => ({ ...p, weight: e.target.value }))} />
              </label>
            </div>
            <div className="form-row form-row--4">
              <label className="field">
                <span>Length</span>
                <input type="number" value={form.length} onChange={(e) => setForm((p) => ({ ...p, length: e.target.value }))} />
              </label>
              <label className="field">
                <span>Width</span>
                <input type="number" value={form.width} onChange={(e) => setForm((p) => ({ ...p, width: e.target.value }))} />
              </label>
              <label className="field">
                <span>Height</span>
                <input type="number" value={form.height} onChange={(e) => setForm((p) => ({ ...p, height: e.target.value }))} />
              </label>
              <label className="field">
                <span>Avg rating</span>
                <input
                  type="number"
                  step="0.1"
                  min={0}
                  value={form.avgRating}
                  onChange={(e) => setForm((p) => ({ ...p, avgRating: e.target.value }))}
                />
              </label>
            </div>
            <div className="form-row form-row--4">
              <label className="field">
                <span>Rating count</span>
                <input type="number" min={0} step={1} value={form.ratingCount} onChange={(e) => setForm((p) => ({ ...p, ratingCount: e.target.value }))} />
              </label>
              <label className="field">
                <span>Meta title</span>
                <input type="text" value={form.metaTitile} onChange={(e) => setForm((p) => ({ ...p, metaTitile: e.target.value }))} />
              </label>
              <label className="field">
                <span>Meta description</span>
                <input type="text" value={form.metaDescription} onChange={(e) => setForm((p) => ({ ...p, metaDescription: e.target.value }))} />
              </label>
              <ImageUploadField label="Image Upload" value={form.image} onChange={(image) => setForm((p) => ({ ...p, image }))} />
            </div>
            {error && <div className="error-banner">{error}</div>}
            {varientError && activeTab === "product" && <div className="error-banner">{varientError}</div>}
            {optionError && activeTab === "product" && <div className="error-banner">{optionError}</div>}
            {imageError && activeTab === "product" && <div className="error-banner">{imageError}</div>}
            {tagError && activeTab === "product" && <div className="error-banner">{tagError}</div>}
            {specError && activeTab === "product" && <div className="error-banner">{specError}</div>}
            {questionError && activeTab === "product" && <div className="error-banner">{questionError}</div>}
            {reviewError && activeTab === "product" && <div className="error-banner">{reviewError}</div>}
            {reviewImageError && activeTab === "product" && <div className="error-banner">{reviewImageError}</div>}
          </form>
        )}

        {activeTab === "variants" && (
          <div className="form-tab-panel">
            {error && <div className="error-banner">{error}</div>}
            {varientError && <div className="error-banner">{varientError}</div>}
            <form
              id="products-varients-form"
              className="form form-fields-bottomline"
              onSubmit={(e) => {
                e.preventDefault();
                void saveAll();
              }}
            >
                <label className="field">
                  <span>SKU</span>
                  <input value={varientForm.skU} onChange={(e) => setVarientForm((p) => ({ ...p, skU: e.target.value }))} />
                </label>
                <label className="field">
                  <span>Barcode</span>
                  <input value={varientForm.barcode} onChange={(e) => setVarientForm((p) => ({ ...p, barcode: e.target.value }))} />
                </label>
                <label className="field">
                  <span>Code</span>
                  <input value={varientForm.code} onChange={(e) => setVarientForm((p) => ({ ...p, code: e.target.value }))} />
                </label>
                <label className="field">
                  <span>Price</span>
                  <input type="number" value={varientForm.price} onChange={(e) => setVarientForm((p) => ({ ...p, price: e.target.value }))} />
                </label>
                <label className="field">
                  <span>Sale Price</span>
                  <input type="number" value={varientForm.salePrice} onChange={(e) => setVarientForm((p) => ({ ...p, salePrice: e.target.value }))} />
                </label>
                <label className="field">
                  <span>Balance Quantity</span>
                  <input
                    type="number"
                    value={varientForm.balanceQuantity}
                    onChange={(e) => setVarientForm((p) => ({ ...p, balanceQuantity: e.target.value }))}
                  />
                </label>
                <label className="field">
                  <span>Weight</span>
                  <input type="number" value={varientForm.weight} onChange={(e) => setVarientForm((p) => ({ ...p, weight: e.target.value }))} />
                </label>
                <label className="field">
                  <span>Length</span>
                  <input type="number" value={varientForm.length} onChange={(e) => setVarientForm((p) => ({ ...p, length: e.target.value }))} />
                </label>
                <label className="field">
                  <span>Width</span>
                  <input type="number" value={varientForm.width} onChange={(e) => setVarientForm((p) => ({ ...p, width: e.target.value }))} />
                </label>
                <label className="field">
                  <span>Height</span>
                  <input type="number" value={varientForm.height} onChange={(e) => setVarientForm((p) => ({ ...p, height: e.target.value }))} />
                </label>
                <label className="field">
                  <span>Created Date</span>
                  <input
                    type="datetime-local"
                    value={varientForm.createdDate}
                    onChange={(e) => setVarientForm((p) => ({ ...p, createdDate: e.target.value }))}
                  />
                </label>
                <label className="field">
                  <span>Updated Date</span>
                  <input
                    type="datetime-local"
                    value={varientForm.updatedDate}
                    onChange={(e) => setVarientForm((p) => ({ ...p, updatedDate: e.target.value }))}
                  />
                </label>
                {(varientEditingId || varientEditParam) && (
                  <div className="form-actions">
                    <button
                      className="btn btn-ghost"
                      type="button"
                      onClick={() => {
                        skipVariantHydrateRef.current = true;
                        setVarientEditingId(null);
                        setVarientForm(emptyVarientForm());
                        const next = new URLSearchParams(searchParams);
                        next.delete("varientEdit");
                        setSearchParams(next, { replace: true });
                      }}
                    >
                      New varient
                    </button>
                  </div>
                )}
              </form>
          </div>
        )}

        {activeTab === "variant-options" && (
          <div className="form-tab-panel">
            {error && <div className="error-banner">{error}</div>}
            {optionError && <div className="error-banner">{optionError}</div>}
            <form
              id="products-varient-options-form"
              className="form form-fields-bottomline"
              onSubmit={(e) => {
                e.preventDefault();
                void saveAll();
              }}
            >
                <SearchableSelectField
                  label="Product varient"
                  options={varientsForProduct}
                  value={optionForm.productVarientId}
                  onChange={(nextValue) => setOptionForm((p) => ({ ...p, productVarientId: nextValue === "" ? "" : Number(nextValue) }))}
                  getOptionLabel={(option) => option.skU || option.code || `Varient #${option.id}`}
                  getSearchText={(option) => `${option.skU ?? ""} ${option.code ?? ""} ${option.barcode ?? ""} ${option.id ?? ""}`}
                  placeholder="Search varient"
                />
                <SearchableSelectField
                  label="Attribute"
                  options={attributes}
                  value={optionForm.attributeId}
                  onChange={(nextValue) =>
                    setOptionForm((p) => ({
                      ...p,
                      attributeId: nextValue === "" ? "" : Number(nextValue),
                      attributeValueId: ""
                    }))
                  }
                  getOptionLabel={(option) => option.name || `Attribute #${option.id}`}
                  getSearchText={(option) => `${option.name ?? ""} ${option.id ?? ""}`}
                  placeholder="Search attribute"
                />
                <SearchableSelectField
                  label="Attribute value"
                  options={attributeValues.filter((av) => !optionForm.attributeId || av.attributeId === Number(optionForm.attributeId))}
                  value={optionForm.attributeValueId}
                  onChange={(nextValue) => setOptionForm((p) => ({ ...p, attributeValueId: nextValue === "" ? "" : Number(nextValue) }))}
                  getOptionLabel={(option) => option.value || `Value #${option.id}`}
                  getSearchText={(option) => `${option.value ?? ""} ${option.id ?? ""}`}
                  placeholder="Search attribute value"
                />
                {(optionEditingId || optionEditParam) && (
                  <div className="form-actions">
                    <button
                      className="btn btn-ghost"
                      type="button"
                      onClick={() => {
                        skipOptionPrefillRef.current = true;
                        setOptionEditingId(null);
                        const firstId = varientsForProduct[0]?.id ?? "";
                        setOptionForm({
                          productVarientId: firstId ? Number(firstId) : "",
                          attributeId: "",
                          attributeValueId: ""
                        });
                        const next = new URLSearchParams(searchParams);
                        next.delete("optionEdit");
                        setSearchParams(next, { replace: true });
                      }}
                    >
                      New option
                    </button>
                  </div>
                )}
              </form>
          </div>
        )}

        {activeTab === "images" && (
          <div className="form-tab-panel">
            {error && <div className="error-banner">{error}</div>}
            {imageError && <div className="error-banner">{imageError}</div>}
            <form
              id="products-images-form"
              className="form form-fields-bottomline"
              onSubmit={(e) => {
                e.preventDefault();
                void saveAll();
              }}
            >
                <ImageUploadField label="Image Upload" value={imageForm.image} onChange={(image) => setImageForm((p) => ({ ...p, image }))} />
                <label className="field">
                  <span>Order</span>
                  <input type="number" value={imageForm.order} onChange={(e) => setImageForm((p) => ({ ...p, order: e.target.value }))} />
                </label>
                {(imageEditingId || imageEditParam) && (
                  <div className="form-actions">
                    <button
                      className="btn btn-ghost"
                      type="button"
                      onClick={() => {
                        skipImageHydrateRef.current = true;
                        setImageEditingId(null);
                        setImageForm(emptyImageForm());
                        const next = new URLSearchParams(searchParams);
                        next.delete("imageEdit");
                        setSearchParams(next, { replace: true });
                      }}
                    >
                      New image
                    </button>
                  </div>
                )}
              </form>
          </div>
        )}

        {activeTab === "tags" && (
          <div className="form-tab-panel">
            {error && <div className="error-banner">{error}</div>}
            {tagError && <div className="error-banner">{tagError}</div>}
            <form
              id="products-tags-form"
              className="form form-fields-bottomline"
              onSubmit={(e) => {
                e.preventDefault();
                void saveAll();
              }}
            >
              <label className="field">
                <span>Tag name</span>
                <input value={tagForm.name} onChange={(e) => setTagForm((p) => ({ ...p, name: e.target.value }))} />
              </label>
              {(tagEditingId || tagEditParam) && (
                <div className="form-actions">
                  <button
                    className="btn btn-ghost"
                    type="button"
                    onClick={() => {
                      skipTagHydrateRef.current = true;
                      setTagEditingId(null);
                      setTagForm(emptyTagForm());
                      const next = new URLSearchParams(searchParams);
                      next.delete("tagEdit");
                      setSearchParams(next, { replace: true });
                    }}
                  >
                    New tag
                  </button>
                </div>
              )}
            </form>
          </div>
        )}

        {activeTab === "specifications" && (
          <div className="form-tab-panel">
            {error && <div className="error-banner">{error}</div>}
            {specError && <div className="error-banner">{specError}</div>}
            <form
              id="products-specifications-form"
              className="form form-fields-bottomline"
              onSubmit={(e) => {
                e.preventDefault();
                void saveAll();
              }}
            >
              <label className="field">
                <span>Specification Id</span>
                <input
                  type="number"
                  value={specForm.specificationId}
                  onChange={(e) => setSpecForm((p) => ({ ...p, specificationId: e.target.value }))}
                />
              </label>
              <label className="field">
                <span>Value</span>
                <input value={specForm.value} onChange={(e) => setSpecForm((p) => ({ ...p, value: e.target.value }))} />
              </label>
              <label className="field">
                <span>Order</span>
                <input type="number" value={specForm.order} onChange={(e) => setSpecForm((p) => ({ ...p, order: e.target.value }))} />
              </label>
              {(specEditingId || specEditParam) && (
                <div className="form-actions">
                  <button
                    className="btn btn-ghost"
                    type="button"
                    onClick={() => {
                      setSpecEditingId(null);
                      setSpecForm(emptySpecForm());
                      const next = new URLSearchParams(searchParams);
                      next.delete("specEdit");
                      setSearchParams(next, { replace: true });
                    }}
                  >
                    New specification
                  </button>
                </div>
              )}
            </form>
          </div>
        )}

        {activeTab === "questions" && (
          <div className="form-tab-panel">
            {error && <div className="error-banner">{error}</div>}
            {questionError && <div className="error-banner">{questionError}</div>}
            <form
              id="products-questions-form"
              className="form form-fields-bottomline"
              onSubmit={(e) => {
                e.preventDefault();
                void saveAll();
              }}
            >
              <div className="form-row form-row--questions">
                <SearchableSelectField
                  label="Customer"
                  options={customers}
                  value={questionForm.customerId}
                  onChange={(nextValue) => setQuestionForm((p) => ({ ...p, customerId: nextValue === "" ? "" : Number(nextValue) }))}
                  getOptionLabel={(option) => option.name || option.userName || `Customer #${option.id}`}
                  getSearchText={(option) => `${option.name ?? ""} ${option.userName ?? ""} ${option.email ?? ""} ${option.id ?? ""}`}
                  placeholder="Search customer"
                />
                <label className="field">
                  <span>Question</span>
                  <textarea rows={3} value={questionForm.question} onChange={(e) => setQuestionForm((p) => ({ ...p, question: e.target.value }))} />
                </label>
              </div>
              <div className="form-row form-row--questions">
                <label className="field field--full">
                  <span>Answer</span>
                  <textarea rows={3} value={questionForm.answer} onChange={(e) => setQuestionForm((p) => ({ ...p, answer: e.target.value }))} />
                </label>
              </div>
              {(questionEditingId || questionEditParam) && (
                <div className="form-actions">
                  <button
                    className="btn btn-ghost"
                    type="button"
                    onClick={() => {
                      skipQuestionHydrateRef.current = true;
                      setQuestionEditingId(null);
                      setQuestionForm(emptyQuestionForm());
                      const next = new URLSearchParams(searchParams);
                      next.delete("questionEdit");
                      setSearchParams(next, { replace: true });
                    }}
                  >
                    New question
                  </button>
                </div>
              )}
            </form>
          </div>
        )}

        {activeTab === "reviews" && (
          <div className="form-tab-panel">
            {error && <div className="error-banner">{error}</div>}
            {reviewError && <div className="error-banner">{reviewError}</div>}
            <form
              id="products-reviews-form"
              className="form form-fields-bottomline"
              onSubmit={(e) => {
                e.preventDefault();
                void saveAll();
              }}
            >
              <div className="form-row form-row--reviews-top">
                <SearchableSelectField
                  label="Order"
                  options={orders}
                  value={reviewForm.orderId}
                  onChange={(nextValue) => setReviewForm((p) => ({ ...p, orderId: nextValue === "" ? "" : Number(nextValue) }))}
                  getOptionLabel={(option) => option.orderNo || `Order #${option.id}`}
                  getSearchText={(option) => `${option.orderNo ?? ""} ${option.id ?? ""}`}
                  placeholder="Search order"
                />
                <SearchableSelectField
                  label="Customer"
                  options={customers}
                  value={reviewForm.customerId}
                  onChange={(nextValue) => setReviewForm((p) => ({ ...p, customerId: nextValue === "" ? "" : Number(nextValue) }))}
                  getOptionLabel={(option) => option.name || option.userName || `Customer #${option.id}`}
                  getSearchText={(option) => `${option.name ?? ""} ${option.userName ?? ""} ${option.email ?? ""} ${option.id ?? ""}`}
                  placeholder="Search customer"
                />
                <label className="field">
                  <span>Rating</span>
                  <input type="number" min={0} max={5} value={reviewForm.rating} onChange={(e) => setReviewForm((p) => ({ ...p, rating: e.target.value }))} />
                </label>
                <label className="field">
                  <span>Created Date</span>
                  <input
                    type="datetime-local"
                    value={reviewForm.createdDate}
                    onChange={(e) => setReviewForm((p) => ({ ...p, createdDate: e.target.value }))}
                  />
                </label>
              </div>
              <div className="form-row form-row--reviews-bottom">
                <label className="field">
                  <span>Updated Date</span>
                  <input
                    type="datetime-local"
                    value={reviewForm.updatedDate}
                    onChange={(e) => setReviewForm((p) => ({ ...p, updatedDate: e.target.value }))}
                  />
                </label>
                <label className="field field--span-3">
                  <span>Review</span>
                  <textarea rows={4} value={reviewForm.review} onChange={(e) => setReviewForm((p) => ({ ...p, review: e.target.value }))} />
                </label>
              </div>
              {(reviewEditingId || reviewEditParam) && (
                <div className="form-actions">
                  <button
                    className="btn btn-ghost"
                    type="button"
                    onClick={() => {
                      skipReviewHydrateRef.current = true;
                      setReviewEditingId(null);
                      setReviewForm(emptyReviewForm());
                      const next = new URLSearchParams(searchParams);
                      next.delete("reviewEdit");
                      setSearchParams(next, { replace: true });
                    }}
                  >
                    New review
                  </button>
                </div>
              )}
            </form>
          </div>
        )}

        {activeTab === "review-images" && (
          <div className="form-tab-panel">
            {error && <div className="error-banner">{error}</div>}
            {reviewImageError && <div className="error-banner">{reviewImageError}</div>}
            <form
              id="products-review-images-form"
              className="form form-fields-bottomline"
              onSubmit={(e) => {
                e.preventDefault();
                void saveAll();
              }}
            >
              <SearchableSelectField
                label="Product review"
                options={productReviews}
                value={reviewImageForm.productReviewId}
                onChange={(nextValue) =>
                  setReviewImageForm((p) => ({ ...p, productReviewId: nextValue === "" ? "" : Number(nextValue) }))
                }
                getOptionLabel={(option) => `Review #${option.id} (rating ${option.rating ?? "—"})`}
                getSearchText={(option) => `${option.id ?? ""} ${option.rating ?? ""} ${option.review ?? ""}`}
                placeholder="Search review"
              />
              <ImageUploadField label="Image Upload" value={reviewImageForm.image} onChange={(image) => setReviewImageForm((p) => ({ ...p, image }))} />
              {(reviewImageEditingId || reviewImageEditParam) && (
                <div className="form-actions">
                  <button
                    className="btn btn-ghost"
                    type="button"
                    onClick={() => {
                      skipReviewImageHydrateRef.current = true;
                      setReviewImageEditingId(null);
                      setReviewImageForm(emptyReviewImageForm());
                      const next = new URLSearchParams(searchParams);
                      next.delete("reviewImageEdit");
                      setSearchParams(next, { replace: true });
                    }}
                  >
                    New review image
                  </button>
                </div>
              )}
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
